// === WORKFLOW SUMMARY — per-product document enrichment ===
// The SAA post-processing chain applied to the core buildCalculationDocument output before
// every offer BFF call (calculate / package / quote). Moved verbatim from
// WorkflowSummaryScreen.tsx; the only seam is `opts.baseUrl` replacing the
// window.location.origin fallback so the chain is golden-testable offline (see enrich.test.ts).
// Pipeline order is load-bearing: Avp -> Inboedel -> Woonhuis -> Personenauto ->
// Rechtsbijstand -> DoorlopendeReis -> coerceNumericFields -> stripSupportsection
// (each later fn relies on earlier discriminator cleanups).
import { GSS_CODE_TO_I18N_KEY } from '../../../utils/familyComposition';
import { applyCaDerivedFields } from '../../../utils/autoGarantie';
import { applyPpDefaults } from '../../../utils/ppDefaults';
import { RBV_PP_REQUEST_DEFAULTS } from '../rechtsbijstand/fields';
import { WOONHUIS_PP_REQUEST_DEFAULTS } from '../woonhuis/fields';
import { orderSaaCoveragesInDocument } from '../../../utils/autoSectionOrder';
import { DOORLOPENDE_REIS_DEFAULTS, DOORLOPENDE_REIS_PP_REQUEST_DEFAULTS, DOORLOPENDE_REIS_CARRIER, DOORLOPENDE_REIS_MYAAND } from '../doorlopendereis/fields';
import { restoreDoorlopendeReisInputs } from '../doorlopendereis/calc';
import { GLASS_MYAAND, hasGlassRebuildValue } from '../../../utils/glassPremium';
import { INBOEDEL_ALLOWED_BA_FIELDS, buildEcRequestRows } from '../inboedel/calc';
import { ONGEVALLEN_DEFAULTS, ONGEVALLEN_PP_REQUEST_DEFAULTS, ONGEVALLEN_CARRIER } from '../ongevallen/fields';
import { normalizeOngevallenVwCodes, restoreOngevallenInputs, restoreOngevallenInsured, sortAfdDocument } from '../ongevallen/calc';
import { AUTO_DOWNSTREAM_OB_ALLOWLIST, LI_KENTEKENHOUDER_PERSONAL, afmkmCode, afmkmWireKm } from '../personenauto/fields';
import { RBV_IN_EIGBVAK_DEFAULT } from '../rechtsbijstand/fields';

export interface EnrichOpts {
  baseUrl: string;
  /**
   * Whether Inboedel may carry glass right now (utils/glassPremium.ts inboedelGlassAllowed:
   * config switch, another product covering glass, Infofolio-known address). Required, not
   * optional: a caller that forgot it would read as "allowed" and put GP back on the wire — the
   * exact failure this seam exists to prevent.
   */
  inboedelGlassAllowed: boolean;
}

/**
 * Clean AVP relation document to match schema before sending to BFF.
 * Whitelists Relatiemantel to only VP (no AL), strips non-schema VP fields,
 * and coerces VP_HUISNR to integer.
 */
export const cleanAvpRelationDocument = (doc: any): any => {
  const rm = doc?.Relatiedocument?.Relatiemantel;
  if (!rm?.VP) return doc;

  // Whitelist VP fields per relation schema
  const vpKeys = ['VP_ANAAM', 'VP_GEBDAT', 'VP_GSSC', 'VP_HUISNR', 'VP_PARZAKC', 'VP_PCODE', 'VP_PLAATS', 'VP_STRAAT', 'VP_TOEVOEG', 'VP_VOORL', 'VP_VOORV', 'VP_GESLACH', 'VP_EMAIL', 'VP_TELNUM', 'VP_INGDTW', 'VP_IDNR', 'VP_ADPSTAT', 'VP_EBRNNM', 'VP_TITULAT', 'VP_AANHEFC'];
  const rawVP = rm.VP;
  const cleanVP: any = {};
  for (const k of vpKeys) {
    if (rawVP[k] !== undefined && rawVP[k] !== null) cleanVP[k] = rawVP[k];
  }
  // VP_HUISNR must be integer
  if (cleanVP.VP_HUISNR !== undefined && typeof cleanVP.VP_HUISNR === 'string') {
    cleanVP.VP_HUISNR = Number(cleanVP.VP_HUISNR);
  }

  // AL section
  const cleanRM: any = { VP: cleanVP };
  if (rm.AL) {
    const alKeys = ['AL_APPLNM', 'AL_URLNAAM'];
    const cleanAL: any = {};
    for (const k of alKeys) {
      if (rm.AL[k] !== undefined) cleanAL[k] = rm.AL[k];
    }
    cleanRM.AL = cleanAL;
  }

  return {
    Relatiedocument: {
      Relatiemantel: cleanRM,
    },
  };
};

/**
 * SAA-specific: Enrich AVP contract items in calculation document.
 * The core buildCalculationDocument strips fields that SAA AVP still needs.
 * This post-processes the document to add them back from workflow data.
 */
export const enrichAvpForSaa = (calculationDocument: any, workflowData: any, t: (key: string) => string, _ppEkst: number, opts: EnrichOpts): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  const appName = calculationDocument?.Relatiemantel?.AL?.AL_APPLNM || 'SAA Verzekeringen';
  const baseUrl = calculationDocument?.Relatiemantel?.AL?.AL_URLNAAM || opts.baseUrl;

  // Find the AVP contract item (has DA section in PP)
  for (const contract of calculationDocument.Contract) {
    if (!contract.PP?.DA) continue;
    // Skip if it has OP (woonhuis) or IP (inboedel) - those aren't AVP
    if (contract.PP.OP || contract.PP.IP) continue;

    // Find AVP workflow data for original values
    let avpData: any = null;
    for (const key of Object.keys(workflowData)) {
      const pp = workflowData[key]?.Contractdocument?.PP;
      if (pp?.DA && !pp.OP && !pp.IP) {
        avpData = workflowData[key];
        break;
      }
    }

    const wfPP = avpData?.Contractdocument?.PP || {};
    const wfAL = avpData?.Contractdocument?.AL || {};

    // --- AL: whitelist only schema-allowed fields ---
    const rawAL = { ...contract.AL };
    const cleanAL: any = {};
    cleanAL.AL_APPLNM = rawAL.AL_APPLNM || wfAL.AL_APPLNM || appName;
    cleanAL.AL_URLNAAM = rawAL.AL_URLNAAM || wfAL.AL_URLNAAM || baseUrl;
    if (rawAL.AL_DATACAT || wfAL.AL_DATACAT) cleanAL.AL_DATACAT = rawAL.AL_DATACAT || wfAL.AL_DATACAT;
    // BY: coerce BY_BYLSRT to string
    const rawBY = rawAL.BY || wfAL.BY || [];
    if (Array.isArray(rawBY) && rawBY.length > 0) {
      const byKeys = ['BY_URLBY', 'BY_BYLOMS', 'BY_BYLSRT'];
      cleanAL.BY = rawBY.map((item: any) => {
        const cleaned: any = {};
        for (const k of byKeys) {
          if (item[k] !== undefined) cleaned[k] = k === 'BY_BYLSRT' ? String(item[k]) : item[k];
        }
        return cleaned;
      });
    }
    contract.AL = cleanAL;

    // --- Restore VP from workflow (whitelist) ---
    const rawVP = (wfPP.VP && Object.keys(wfPP.VP).length > 0) ? wfPP.VP : contract.PP.VP || {};
    const vpKeys = ['VP_ANAAM', 'VP_VOORL', 'VP_VOORV', 'VP_STRAAT', 'VP_HUISNR', 'VP_TOEVOEG', 'VP_PLAATS', 'VP_PCODE', 'VP_GEBDAT', 'VP_PARZAKC'];
    const cleanVP: any = {};
    for (const k of vpKeys) { if (rawVP[k] !== undefined) cleanVP[k] = rawVP[k]; }
    contract.PP.VP = cleanVP;

    // --- VZ: restore + ensure required fields ---
    const rawVZ = contract.PP.VZ || wfPP.VZ || {};
    const vzKeys = ['VZ_RELSRTC', 'VZ_VOORL', 'VZ_VOORV', 'VZ_GESLACH', 'VZ_GEBDAT', 'VZ_ANAAM'];
    const cleanVZ: any = {};
    for (const k of vzKeys) { if (rawVZ[k] !== undefined) cleanVZ[k] = rawVZ[k]; }
    if (!cleanVZ.VZ_RELSRTC) cleanVZ.VZ_RELSRTC = '1';
    if (!cleanVZ.VZ_VOORL && cleanVP.VP_VOORL) cleanVZ.VZ_VOORL = cleanVP.VP_VOORL;
    if (!cleanVZ.VZ_ANAAM && cleanVP.VP_ANAAM) cleanVZ.VZ_ANAAM = cleanVP.VP_ANAAM;
    if (cleanVZ.VZ_GEBDAT === undefined && cleanVP.VP_GEBDAT !== undefined) cleanVZ.VZ_GEBDAT = cleanVP.VP_GEBDAT;
    contract.PP.VZ = cleanVZ;

    // --- RI: ensure RI_ALGVRWD ---
    const riKeys = ['RI_EGD', 'RI_OGB', 'RI_POLJUS', 'RI_OVRMED', 'RI_CGGDA', 'RI_STVOOP', 'RI_ALGVRWD'];
    const rawRI = contract.PP.RI || wfPP.RI || {};
    const cleanRI: any = {};
    for (const k of riKeys) { cleanRI[k] = rawRI[k] ?? 'N'; }
    contract.PP.RI = cleanRI;

    // --- DA: whitelist + ensure MP --- merge workflow DA values for fields core may strip
    const daKeys = ['MP', 'DA_VOLGNUM', 'DA_CODE', 'DA_GSS', 'DA_VERZSOM', 'DA_ERB', 'DA_BTP', 'DA_PPRC', 'DA_GEBIED', 'DA_WPREMBP', 'DA_ENTREF', 'DA_NTP', 'DA_TASS', 'DA_TPP', 'DA_TTOT', 'DA_TTPPRV', 'DA_TTSLTB', 'DA_PTSLTB', 'DA_PKRTVMP', 'DA_TKRT', 'DA_TTSL', 'DA_TPKRTVM'];
    const rawDA = contract.PP.DA || {};
    const wfDA = wfPP.DA || {};
    const cleanDA: any = {};
    for (const k of daKeys) {
      if (rawDA[k] !== undefined) cleanDA[k] = rawDA[k];
      else if (wfDA[k] !== undefined) cleanDA[k] = wfDA[k];
    }
    if (!cleanDA.MP) cleanDA.MP = [];
    if (!cleanDA.DA_WPREMBP) cleanDA.DA_WPREMBP = '4';
    if (typeof cleanDA.DA_WPREMBP !== 'string') cleanDA.DA_WPREMBP = String(cleanDA.DA_WPREMBP);
    contract.PP.DA = cleanDA;

    // --- VL: ensure VL_MYAAND ---
    const rawVL = (wfPP.VL && Object.keys(wfPP.VL).length > 0) ? wfPP.VL : contract.PP.VL || {};
    const cleanVL: any = {};
    if (rawVL.VL_VOLGNUM !== undefined) cleanVL.VL_VOLGNUM = rawVL.VL_VOLGNUM;
    cleanVL.VL_MYAAND = rawVL.VL_MYAAND || contract.PP.PP_MYAAND || 'R515';
    contract.PP.VL = cleanVL;

    // --- VE: ensure VE_MYAAND ---
    const rawVE = contract.PP.VE || wfPP.VE || {};
    const cleanVE: any = {};
    if (rawVE.VE_VOLGNUM !== undefined) cleanVE.VE_VOLGNUM = rawVE.VE_VOLGNUM;
    cleanVE.VE_MYAAND = rawVE.VE_MYAAND || 'IP01';
    contract.PP.VE = cleanVE;

    // --- CL: whitelist items ---
    const clKeys = ['CL_VOLGNUM', 'CL_CLAUSNR', 'CL_CLAUSOM', 'CL_CLAUTXT', 'CL_CLAUVRW', 'CL_VERSIEC', 'CL_VRWRKCD'];
    if (Array.isArray(contract.PP.CL)) {
      contract.PP.CL = contract.PP.CL.map((item: any) => {
        const cleaned: any = {};
        for (const k of clKeys) { if (item[k] !== undefined) cleaned[k] = item[k]; }
        return cleaned;
      });
    }

    // --- VW: whitelist items ---
    const srcVW = (wfPP.VW && Array.isArray(wfPP.VW)) ? wfPP.VW : contract.PP.VW || [];
    const vwKeys = ['VW_VOLGNUM', 'VW_VRWNR', 'VW_VRWOM', 'VW_VRWVERS', 'VW_URL', 'VW_BRONVRW', 'VW_VRWRKCD'];
    if (Array.isArray(srcVW)) {
      contract.PP.VW = srcVW.map((item: any) => {
        const cleaned: any = {};
        for (const k of vwKeys) { if (item[k] !== undefined) cleaned[k] = item[k]; }
        return cleaned;
      });
    }

    // --- TP ---
    contract.PP.TP = {};

    // --- QS ---
    contract.PP.QS = { QA: contract.PP.QS?.QA || wfPP.QS?.QA || [] };

    // --- PP scalar fields: whitelist ---
    const ppScalarKeys = [
      'PP_EXTERN', 'PP_BRANCHE', 'PP_MYAAND', 'PP_INGDAT', 'PP_CDUUMND',
      'PP_HVVDAT', 'PP_BETTERM', 'PP_INGDTW', 'PP_STATUS', 'PP_AFDDEFN',
      'PP_ADEFVRS', 'PP_SPECLIM', 'PP_HANDACC', 'PP_PRODNM', 'PP_PRODUCT', 'PP_CONTROM', 'PP_CVS', 'PP_COLL',
      'PP_AGNTNR', 'PP_EKST', 'PP_PKST', 'PP_NTP', 'PP_BTP', 'PP_TASS',
      'PP_TPP', 'PP_TTOT', 'PP_TTPPRV', 'PP_TTSLTB', 'PP_PTSLTB',
      'PP_PKRTVMP', 'PP_TKRT', 'PP_TTSL', 'PP_AFWAFSP', 'PP_AFWAFST',
      'PP_SPLIMNR', 'PP_PVADAT', 'PP_EXPDAT', 'PP_MYNAAM', 'PP_WPREMBP',
      'PP_TPKRTVM', 'PP_TARDAT', 'PP_INCWIJZ', 'PP_IBAN', 'PP_SLOTAKK',
      'PP_STPRV', 'PP_PPRC',
    ];
    const rawPP = contract.PP;
    const cleanPP: any = {};
    for (const k of ppScalarKeys) {
      if (rawPP[k] !== undefined) cleanPP[k] = rawPP[k];
      else if (wfPP[k] !== undefined) cleanPP[k] = wfPP[k];
    }

    // Ensure required PP fields & defaults
    if (cleanPP.PP_INCWIJZ === undefined) cleanPP.PP_INCWIJZ = wfPP.PP_INCWIJZ || 'I';
    if (cleanPP.PP_IBAN === undefined) cleanPP.PP_IBAN = wfPP.PP_IBAN || '';
    if (cleanPP.PP_WPREMBP === undefined) cleanPP.PP_WPREMBP = '4';
    if (typeof cleanPP.PP_WPREMBP !== 'string') cleanPP.PP_WPREMBP = String(cleanPP.PP_WPREMBP);
    cleanPP.PP_EKST = 0;
    cleanPP.PP_PPRC = 20;
    cleanPP.PP_STPRV = 'J';
    if (cleanPP.PP_TPP === undefined) cleanPP.PP_TPP = 0;
    // PP_PRODUCT from PP_PRODNM if missing
    if (!cleanPP.PP_PRODUCT && cleanPP.PP_PRODNM) cleanPP.PP_PRODUCT = cleanPP.PP_PRODNM;
    if (!cleanPP.PP_PRODUCT && wfPP.PP_PRODUCT) cleanPP.PP_PRODUCT = wfPP.PP_PRODUCT;
    // PP_CONTROM from GSS code if missing
    if (!cleanPP.PP_CONTROM) {
      const gssKey = GSS_CODE_TO_I18N_KEY[contract.PP.DA?.DA_GSS] || GSS_CODE_TO_I18N_KEY[contract.PP.VP?.VP_GSSC];
      if (gssKey) cleanPP.PP_CONTROM = t(gssKey);
    }

    // Type coercions
    if (cleanPP.PP_PVADAT !== undefined && typeof cleanPP.PP_PVADAT !== 'string') cleanPP.PP_PVADAT = String(cleanPP.PP_PVADAT);
    if (cleanPP.PP_EXPDAT !== undefined && typeof cleanPP.PP_EXPDAT !== 'string') cleanPP.PP_EXPDAT = String(cleanPP.PP_EXPDAT);

    // Re-attach sub-objects
    cleanPP.RI = contract.PP.RI;
    cleanPP.VP = contract.PP.VP;
    cleanPP.VZ = contract.PP.VZ;
    cleanPP.DA = contract.PP.DA;
    cleanPP.VL = contract.PP.VL;
    cleanPP.VE = contract.PP.VE;
    cleanPP.CL = contract.PP.CL;
    cleanPP.VW = contract.PP.VW;
    cleanPP.TP = contract.PP.TP;
    cleanPP.QS = contract.PP.QS;

    contract.PP = cleanPP;
  }

  return calculationDocument;
};

/**
 * SAA-specific: Clean inboedel contract items in calculation document.
 * Applies PP field whitelist, ensures IP/EC are arrays, fixes types.
 * Similar pattern to enrichAvpForSaa but for inboedel product.
 */
export const enrichInboedelForSaa = (calculationDocument: any, workflowData: any, _ppEkst: number, opts: EnrichOpts): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  const appName = calculationDocument?.Relatiemantel?.AL?.AL_APPLNM || 'SAA Verzekeringen';
  const baseUrl = calculationDocument?.Relatiemantel?.AL?.AL_URLNAAM || opts.baseUrl;

  // Find inboedel workflow data for restoring BA (core buildCalculationDocument strips BA from PP)
  let inboedelWorkflowData: any = null;
  if (workflowData) {
    for (const key of Object.keys(workflowData)) {
      const pp = workflowData[key]?.Contractdocument?.PP;
      if (pp?.IP && !pp.DA) {
        inboedelWorkflowData = workflowData[key];
        break;
      }
    }
  }

  for (const contract of calculationDocument.Contract) {
    // Identify inboedel contract: has IP (array or object) but NOT DA (that's AVP)
    const hasIP = contract.PP?.IP || (Array.isArray(contract.PP?.IP) && contract.PP.IP.length > 0);
    if (!hasIP || contract.PP?.DA) continue;

    const pp = contract.PP;

    // Restore BA from workflow data — core buildCalculationDocument deletes BA from PP,
    // but inboedel schema requires BA in PP for address/building data used in premium calculation
    if (!pp.BA && inboedelWorkflowData?.Contractdocument?.PP?.BA) {
      pp.BA = JSON.parse(JSON.stringify(inboedelWorkflowData.Contractdocument.PP.BA));
    }

    // OP (Eigenaarsbelang) - restore from workflow data if missing
    if (!pp.OP && inboedelWorkflowData?.Contractdocument?.PP?.OP) {
      pp.OP = JSON.parse(JSON.stringify(inboedelWorkflowData.Contractdocument.PP.OP));
    }

    // Restore IB from _uiState (InboedelScreen moves IB_INBVST to _uiState on save)
    const uiState = inboedelWorkflowData?._uiState || {};
    const dekkingen = inboedelWorkflowData?.Supportsection?.Dekkingen || uiState.Dekkingen || {};
    if (!pp.IB && uiState.IB_INBVST) {
      pp.IB = { IB_INBVST: uiState.IB_INBVST };
    }

    // PP field whitelist (matches inboedel jsonschema.json, additionalProperties: false)
    const allowedPP = new Set([
      'BA', 'BI', 'CL', 'EC', 'GA', 'GP', 'HU', 'IB', 'IP', 'KB', 'LS', 'OP', 'RI',
      'TP', 'VE', 'VL', 'VP', 'VR', 'VW', 'QS',
      'PP_ADEFVRS', 'PP_AFDDEFN', 'PP_AFWAFST', 'PP_AGNTNR',
      'PP_BETTERM', 'PP_BRANCHE', 'PP_COLL', 'PP_CDUUMND', 'PP_CVS', 'PP_EKST',
      'PP_EXPDAT', 'PP_EXTERN', 'PP_HANDACC', 'PP_HVVDAT', 'PP_INCWIJZ', 'PP_INGDAT', 'PP_INGDTW',
      'PP_MYAAND', 'PP_MYNAAM', 'PP_NTP', 'PP_NTPVPK', 'PP_PKRTVMP', 'PP_PPRC', 'PP_PRODNM', 'PP_PRODUCT', 'PP_PTSLTB',
      'PP_PVADAT', 'PP_RDNMUT', 'PP_SPECLIM', 'PP_SPLIMNR', 'PP_STATUS', 'PP_STPRV',
      'PP_TARDAT', 'PP_TASS', 'PP_TTOT', 'PP_TTPPRV',
      'PP_TTSLTB', 'PP_VRWRKCD', 'PP_WPREMBP',
    ]);
    for (const key of Object.keys(pp)) {
      // Preserve the _discountBaked marker so downstream pre-package discount blocks can
      // detect already-discounted data and skip double-applying.
      if (!allowedPP.has(key) && key !== '_discountBaked') delete pp[key];
    }

    // VP: VP_GEBDAT, VP_PARZAKC, VP_VRWRKCD allowed
    if (pp.VP) {
      const { VP_GEBDAT, VP_PARZAKC, VP_VRWRKCD } = pp.VP;
      pp.VP = {
        VP_PARZAKC: VP_PARZAKC || 'P',
        ...(VP_GEBDAT !== undefined ? { VP_GEBDAT: Number(VP_GEBDAT) } : {}),
        ...(VP_VRWRKCD !== undefined ? { VP_VRWRKCD } : {}),
      };
    }

    // BA: whitelist and fix BA_HUISNR type (must be integer)
    if (pp.BA) {
      // Restore BA_MONUMEN, BA_OPPWON and BA_BOUWJR from _uiState (InboedelScreen moves these
      // to _uiState on save). BA_BOUWJR aligns the summary chain with the screen chain
      // (inboedel/calc.ts) — the inboedel schema requires it under additionalProperties:false.
      if (pp.BA.BA_MONUMEN === undefined && uiState.BA_MONUMEN !== undefined) {
        pp.BA.BA_MONUMEN = uiState.BA_MONUMEN;
      }
      if (pp.BA.BA_OPPWON === undefined && uiState.BA_OPPWON !== undefined) {
        pp.BA.BA_OPPWON = uiState.BA_OPPWON;
      }
      if (pp.BA.BA_BOUWJR === undefined && uiState.BA_BOUWJR !== undefined) {
        pp.BA.BA_BOUWJR = uiState.BA_BOUWJR;
      }
      // BA_HERBWRD rates the Midglas glass premium; step data saved before the field was
      // accepted on the Inboedel wire carries it in the _uiState stash only.
      if (pp.BA.BA_HERBWRD === undefined && uiState.BA_HERBWRD !== undefined) {
        pp.BA.BA_HERBWRD = uiState.BA_HERBWRD;
      }

      for (const key of Object.keys(pp.BA)) {
        if (!INBOEDEL_ALLOWED_BA_FIELDS.has(key)) delete pp.BA[key];
      }
      if (pp.BA.BA_HUISNR !== undefined) {
        pp.BA.BA_HUISNR = parseInt(String(pp.BA.BA_HUISNR), 10);
      }
      if (pp.BA.BA_HERBWRD !== undefined) {
        pp.BA.BA_HERBWRD = Number(pp.BA.BA_HERBWRD);
      }
    }

    // IP: keep as object (matches productservice/calculate response format)
    if (pp.IP) {
      if (Array.isArray(pp.IP)) pp.IP = pp.IP[0] || {};
      if (pp.IP['0']) delete pp.IP['0'];
      if (pp.IP.IP_PPRC !== undefined) pp.IP.IP_PPRC = Number(pp.IP.IP_PPRC);
    }

    // EC: ensure array format (schema requires type "array")
    if (pp.EC && !Array.isArray(pp.EC)) {
      if (pp.EC['0']) delete pp.EC['0'];
      if (pp.EC.EC_PPRC !== undefined) pp.EC.EC_PPRC = Number(pp.EC.EC_PPRC);
      pp.EC = [pp.EC];
    }
    // ONE EC row (schema maxItems 1): Smart Home replaces Mobiele elektronica. Rebuilt from the
    // selection like the screen's mappers, so a document stored before the rule (two rows)
    // cannot reach the package calculation.
    const ecRows = buildEcRequestRows(dekkingen, pp.EC);
    if (ecRows.length > 0) pp.EC = ecRows; else delete pp.EC;
    // EC entries: ensure EC_ENTREF, EC_VOLGNUM, and EC_ERB as number
    if (Array.isArray(pp.EC)) {
      for (const ec of pp.EC) {
        if (!ec.EC_ENTREF && ec.EC_CODE) ec.EC_ENTREF = ec.EC_CODE;
        if (ec.EC_VOLGNUM === undefined) ec.EC_VOLGNUM = 1;
        if (ec.EC_ERB !== undefined) ec.EC_ERB = Number(ec.EC_ERB);
        if (!ec.EC_GEBIED) ec.EC_GEBIED = 'W';
      }
    }

    // BI_VERZSOM: must be exactly 7500 or 15000 (oneOf const)
    if (pp.BI?.BI_VERZSOM !== undefined && ![7500, 15000].includes(pp.BI.BI_VERZSOM)) {
      pp.BI.BI_VERZSOM = 7500;
    }

    // Type enforcement: PPRC fields must be numbers
    for (const section of ['BI', 'GP', 'HU', 'KB', 'LS']) {
      if (pp[section]?.[`${section}_PPRC`] !== undefined) {
        pp[section][`${section}_PPRC`] = Number(pp[section][`${section}_PPRC`]);
      }
    }
    // VR (Verhuur) not offered for SAA - remove if present
    delete pp.VR;

    // GP (Glas) goes out ONLY when selected, allowed for this address (inboedelGlassAllowed)
    // and rated over a present rebuild value. Not "unless deselected": the screen's mappers are
    // not the only source of this document. applyProductDefaults seeds PP.GP into the SAVED
    // step data (inboedel/calc.ts) and the PP whitelist above keeps it, so an unselected or
    // unratable GP would ride into the package calculation, where one section Midglas cannot
    // rate declines the WHOLE package (XG 11) and every price is lost, not just glass.
    if (dekkingen.Glas !== 'J' || !opts.inboedelGlassAllowed || !hasGlassRebuildValue(pp.BA?.BA_HERBWRD)) {
      delete pp.GP;
    }

    // PP-level type enforcement
    if (pp.PP_CDUUMND !== undefined) pp.PP_CDUUMND = Number(pp.PP_CDUUMND);
    if (pp.PP_HVVDAT !== undefined) pp.PP_HVVDAT = Number(pp.PP_HVVDAT);
    if (pp.PP_INGDAT !== undefined) pp.PP_INGDAT = Number(pp.PP_INGDAT);
    if (pp.PP_BETTERM !== undefined) pp.PP_BETTERM = Number(pp.PP_BETTERM);
    pp.PP_PPRC = 20;
    // PP_EKST - 0 at product level; charged once at package level (PK_TINV)
    pp.PP_EKST = 0;
    pp.PP_STPRV = 'J';

    // Default *_WPREMBP='4' across all Inboedel coverage sections.
    if (pp.IP && !pp.IP.IP_WPREMBP) pp.IP.IP_WPREMBP = '4';
    if (pp.BI && !pp.BI.BI_WPREMBP) pp.BI.BI_WPREMBP = '4';
    if (pp.HU && !pp.HU.HU_WPREMBP) pp.HU.HU_WPREMBP = '4';
    if (pp.KB && !pp.KB.KB_WPREMBP) pp.KB.KB_WPREMBP = '4';
    if (pp.LS && !pp.LS.LS_WPREMBP) pp.LS.LS_WPREMBP = '4';
    if (pp.GA && !pp.GA.GA_WPREMBP) pp.GA.GA_WPREMBP = '4';
    if (pp.GP && !pp.GP.GP_WPREMBP) pp.GP.GP_WPREMBP = '4';
    if (pp.GP && !pp.GP.GP_MYAAND) pp.GP.GP_MYAAND = GLASS_MYAAND;
    if (Array.isArray(pp.EC)) pp.EC.forEach((ec: any) => { if (!ec.EC_WPREMBP) ec.EC_WPREMBP = '4'; });

    // Required empty objects (additionalProperties: false, no properties defined)
    if (!pp.TP || typeof pp.TP !== 'object') pp.TP = {};
    if (!pp.VL || typeof pp.VL !== 'object') pp.VL = {};
    if (!pp.CL || !Array.isArray(pp.CL)) pp.CL = [];
    if (!pp.QS) pp.QS = { QA: [] };
    if (!pp.QS.QA) pp.QS.QA = [];
    // TP and VL must be empty objects
    for (const key of Object.keys(pp.TP)) delete pp.TP[key];
    for (const key of Object.keys(pp.VL)) delete pp.VL[key];

    // Fix AL: ensure AL_APPLNM and AL_URLNAAM are set
    contract.AL = {
      AL_APPLNM: appName,
      AL_URLNAAM: baseUrl,
      ...contract.AL,
    };
  }

  return calculationDocument;
};

/**
 * SAA-specific: Fix woonhuis contract items in calculation document.
 * Restores BA from workflow, ensures premium fields are numbers, ensures VR exists.
 */
export const enrichWoonhuisForSaa = (calculationDocument: any, workflowData: any, _ppEkst: number): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  // Find woonhuis workflow data
  let woonhuisWorkflowData: any = null;
  if (workflowData) {
    for (const key of Object.keys(workflowData)) {
      const pp = workflowData[key]?.Contractdocument?.PP;
      if (pp?.OP && !pp.IP && !pp.DA) {
        woonhuisWorkflowData = workflowData[key];
        break;
      }
    }
  }

  for (const contract of calculationDocument.Contract) {
    // Identify woonhuis contract: has OP but NOT IP (inboedel) and NOT DA (AVP)
    if (!contract.PP?.OP || contract.PP?.IP || contract.PP?.DA) continue;

    // Strip Contractdocument-level extras the BFF re-introduces on response merges
    // (NT, XG are response-only; Supportsection is UI-state; Relatiedocument belongs at doc root).
    delete contract.NT;
    delete contract.XG;
    delete contract.Supportsection;
    delete contract.Relatiedocument;

    const pp = contract.PP;

    // Restore BA from workflow data (core buildCalculationDocument strips BA from PP)
    if (!pp.BA && woonhuisWorkflowData?.Contractdocument?.PP?.BA) {
      pp.BA = JSON.parse(JSON.stringify(woonhuisWorkflowData.Contractdocument.PP.BA));
    }
    // BA_HERBWRD stays: the woonhuis schema requires it and the Midglas engine rates GP over it.
    if (pp.BA?.BA_HERBWRD !== undefined) pp.BA.BA_HERBWRD = Number(pp.BA.BA_HERBWRD);

    // Ensure section premium fields are numbers (BFF returns strings)
    const op = Array.isArray(pp.OP) ? pp.OP[0] : pp.OP;
    if (op) {
      if (op.OP_NTP !== undefined) op.OP_NTP = Number(op.OP_NTP) || 0;
      if (op.OP_TPP !== undefined) op.OP_TPP = Number(op.OP_TPP) || 0;
      if (op.OP_TASS !== undefined) op.OP_TASS = Number(op.OP_TASS) || 0;
      if (op.OP_TTOT !== undefined) op.OP_TTOT = Number(op.OP_TTOT) || 0;
    }
    if (pp.GP) {
      if (pp.GP.GP_NTP !== undefined) pp.GP.GP_NTP = Number(pp.GP.GP_NTP) || 0;
      if (pp.GP.GP_TPP !== undefined) pp.GP.GP_TPP = Number(pp.GP.GP_TPP) || 0;
      if (pp.GP.GP_TASS !== undefined) pp.GP.GP_TASS = Number(pp.GP.GP_TASS) || 0;
    }
    // VR (Verhuur) not offered for SAA - remove if BFF re-introduced it
    delete pp.VR;

    // Recalculate PP-level totals from sections (core may have computed with string values)
    const opNtp = op?.OP_NTP || 0;
    const gpNtp = pp.GP?.GP_NTP || 0;
    pp.PP_NTP = opNtp + gpNtp;
    pp.PP_NTPVPK = pp.PP_NTP;
    pp.PP_TPP = (op?.OP_TPP || 0) + (pp.GP?.GP_TPP || 0);
    pp.PP_TASS = (op?.OP_TASS || 0) + (pp.GP?.GP_TASS || 0);
    if (pp.PP_TTOT !== undefined && pp.PP_TTOT !== null) {
      pp.PP_TTOT = Number(pp.PP_TTOT) || 0;
    }
    // PP_EKST - 0 at product level; charged once at package level (PK_TINV)
    pp.PP_EKST = 0;
    pp.PP_STPRV = 'J';
    applyPpDefaults(pp, WOONHUIS_PP_REQUEST_DEFAULTS);

    // Default *_WPREMBP='4' on the Woonhuis coverage sections that define it.
    // Not GP: woonhuis.json's GP block has no GP_WPREMBP property and is
    // additionalProperties:false, so adding it would be invalid.
    if (pp.OP) {
      if (Array.isArray(pp.OP)) pp.OP.forEach((o: any) => { if (!o.OP_WPREMBP) o.OP_WPREMBP = '4'; });
      else if (!pp.OP.OP_WPREMBP) pp.OP.OP_WPREMBP = '4';
    }
    if (pp.GP && !pp.GP.GP_MYAAND) pp.GP.GP_MYAAND = GLASS_MYAAND;
  }

  return calculationDocument;
};

/**
 * SAA-specific: Clean personenauto contract items in calculation document.
 * Applies whitelists per section to strip BFF response fields that the schema doesn't allow.
 */
export const enrichPersonenautoForSaa = (calculationDocument: any, _ppEkst: number): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  for (const contract of calculationDocument.Contract) {
    // Identify personenauto contract: has OB section
    if (!contract.PP?.OB) continue;

    const pp = contract.PP;

    // Strip Contractdocument-level extras (NT, XG are response-only; Relatiedocument belongs at doc root)
    delete contract.NT;
    delete contract.XG;
    delete contract.Supportsection;
    delete contract.Relatiedocument;

    // AL: strip non-schema fields, keep AL_APPLNM, AL_URLNAAM and BY
    if (contract.AL) {
      delete contract.AL.AL_VRWRKCD;
      delete contract.AL.AL_CPNAAM;
      // Keep BY array (bijlagen) - required for request
      if (contract.AL.BY && Array.isArray(contract.AL.BY)) {
        const allowedBY = new Set(['BY_BYLOMS', 'BY_BYLSRT', 'BY_URLBY']);
        contract.AL.BY = contract.AL.BY.map((item: any) => {
          const cleaned: any = {};
          for (const key of Object.keys(item)) {
            if (allowedBY.has(key)) cleaned[key] = item[key];
          }
          return cleaned;
        });
      }
    }

    // BS: whitelist (full personal + address fields)
    if (pp.BS) {
      const allowedBS = new Set([
        'BS_ANAAM', 'BS_GEBDAT', 'BS_GESLACH', 'BS_HUISNR', 'BS_PCODE',
        'BS_PLAATS', 'BS_RELSRTC', 'BS_STRAAT', 'BS_TOEVOEG', 'BS_VOORL', 'BS_VOORV',
      ]);
      for (const key of Object.keys(pp.BS)) {
        if (!allowedBS.has(key)) delete pp.BS[key];
      }
    }

    // OB: whitelist. OB_EERSTEI is the first-owner flag — must survive the package calc and
    // the final request so Allianz can validate Nieuwwaarderegeling (CG 2040) /
    // Aanschafwaarderegeling (CG 2041) on submission.
    if (pp.OB) {
      for (const key of Object.keys(pp.OB)) {
        if (!AUTO_DOWNSTREAM_OB_ALLOWLIST.has(key)) delete pp.OB[key];
      }
      // Fix types: these must be numbers, not strings
      const ob = pp.OB;
      if (ob.OB_ZITPLAA !== undefined) ob.OB_ZITPLAA = parseInt(String(ob.OB_ZITPLAA), 10) || 0;
      if (ob.OB_AFMWE !== undefined) ob.OB_AFMWE = parseInt(String(ob.OB_AFMWE), 10) || 0;
      // OB_AFMKM: the engine rates real kilometers — the wire carries the band km
      // (personenauto/fields.ts AFMKM table). The dropdown code rides along in
      // EXPERIMENT(OB_AFMKMCD) — self-invented label, safe to remove, see fields.ts.
      if (ob.OB_AFMKM !== undefined) {
        const code = afmkmCode(ob.OB_AFMKMCD ?? ob.OB_AFMKM);
        if (code) ob.OB_AFMKMCD = code;
        ob.OB_AFMKM = afmkmWireKm(ob.OB_AFMKM);
      }
      if (ob.OB_AFMCC !== undefined) ob.OB_AFMCC = parseInt(String(ob.OB_AFMCC), 10) || 0;
      if (ob.OB_ACCELER !== undefined) ob.OB_ACCELER = parseFloat(String(ob.OB_ACCELER)) || 0;
      if (ob.OB_DAGWRDE !== undefined) ob.OB_DAGWRDE = parseInt(String(ob.OB_DAGWRDE), 10) || 0;
    }

    // VE: whitelist (VE_MYAAND only)
    if (pp.VE) {
      const allowedVE = new Set(['VE_MYAAND']);
      for (const key of Object.keys(pp.VE)) {
        if (!allowedVE.has(key)) delete pp.VE[key];
      }
    }

    // VP: whitelist
    if (pp.VP) {
      const allowedVP = new Set(['VP_GEBDAT', 'VP_HUISNR', 'VP_PCODE', 'VP_PLAATS', 'VP_STRAAT', 'VP_TOEVOEG']);
      for (const key of Object.keys(pp.VP)) {
        if (!allowedVP.has(key)) delete pp.VP[key];
      }
    }

    // WA: whitelist + ensure MP array
    if (pp.WA) {
      if (!pp.WA.MP) pp.WA.MP = [];
      if (!pp.WA.WA_WPREMBP) pp.WA.WA_WPREMBP = '4';
      const allowedWA = new Set([
        'MP', 'WA_BMCOMTR', 'WA_BMEXTRA', 'WA_BMNAAND', 'WA_BMPERC', 'WA_BMTABCD', 'WA_BTP',
        'WA_CODE', 'WA_ENTREF', 'WA_NCAAND', 'WA_NCPRC', 'WA_NTP', 'WA_PKRTVMP',
        'WA_PPRC', 'WA_PTSLTB', 'WA_SCHDVRY', 'WA_TASS', 'WA_TKRT', 'WA_TKRTBM',
        'WA_TPP', 'WA_TTOT', 'WA_TTPPRV', 'WA_TTSL', 'WA_TTSLBM', 'WA_TTSLTB',
        'WA_VZBLETS', 'WA_VZBMATS', 'WA_WPREMBP',
      ]);
      for (const key of Object.keys(pp.WA)) {
        if (!allowedWA.has(key)) delete pp.WA[key];
      }
    }

    // Default *_WPREMBP='4' across all PA coverage sections (schema-required per coverage).
    if (Array.isArray(pp.CG)) {
      pp.CG.forEach((cg: any) => { if (!cg.CG_WPREMBP) cg.CG_WPREMBP = '4'; });
    }
    if (Array.isArray(pp.CA)) {
      pp.CA.forEach((ca: any) => { if (!ca.CA_WPREMBP) ca.CA_WPREMBP = '4'; });
    }
    if (pp.RP && !pp.RP.RP_WPREMBP) pp.RP.RP_WPREMBP = '4';
    if (pp.SV && !pp.SV.SV_WPREMBP) pp.SV.SV_WPREMBP = '4';
    if (pp.PH && !pp.PH.PH_WPREMBP) pp.PH.PH_WPREMBP = '4';

    // VW: whitelist each item
    if (Array.isArray(pp.VW)) {
      const allowedVW = new Set(['VW_VOLGNUM', 'VW_VRWNR', 'VW_VRWOM', 'VW_VRWVERS', 'VW_URL']);
      pp.VW = pp.VW.map((item: any) => {
        const cleaned: any = {};
        for (const key of Object.keys(item)) {
          if (allowedVW.has(key)) cleaned[key] = item[key];
        }
        return cleaned;
      });
    } else {
      pp.VW = [];
    }

    // Required empty sections
    if (!pp.CL || !Array.isArray(pp.CL)) pp.CL = [];
    if (!pp.TP || typeof pp.TP !== 'object') pp.TP = {};
    if (!pp.VL || typeof pp.VL !== 'object') pp.VL = {};
    // TP and VL must be empty objects
    for (const key of Object.keys(pp.TP)) delete pp.TP[key];
    for (const key of Object.keys(pp.VL)) delete pp.VL[key];

    // Ensure required sections exist
    if (!pp.LC) pp.LC = { LC_NUMMER: '' };
    if (!pp.LI) pp.LI = {};
    for (const k of LI_KENTEKENHOUDER_PERSONAL) delete pp.LI[k];
    if (!pp.LP) pp.LP = { LP_ANAAM: '' };
    if (!pp.RI) pp.RI = { RI_ALGVRWD: 'J' };
    if (!pp.CA) pp.CA = [];
    if (!pp.CG) pp.CG = [];

    // CA_BTWMVZ from OB_BTWCD + garantie flags — single source (autoGarantie.ts).
    applyCaDerivedFields(pp);

    // Ensure required PP scalar fields
    if (pp.PP_IBAN === undefined) pp.PP_IBAN = '';
    if (pp.PP_SPLIMNR === undefined) pp.PP_SPLIMNR = '';
    if (pp.PP_BMEXTRA === undefined) pp.PP_BMEXTRA = 0;
    if (!pp.PP_HANDACC) pp.PP_HANDACC = 'N';
    if (!pp.PP_INCWIJZ) pp.PP_INCWIJZ = 'I';
    if (!pp.PP_SPECLIM) pp.PP_SPECLIM = 'N';
    delete pp.PP_SCHDVRM;
    // PP_EKST - 0 at product level; charged once at package level (PK_TINV)
    pp.PP_EKST = 0;
    if (!pp.PP_STPRV) pp.PP_STPRV = 'N';

    // Keep selected optional coverages (CA, RP, SV, CG) - they were already filtered in PersonenAutoScreen handleNext
    // Only remove PH (disabled - UWM15)
    delete pp.PH;

    // PP_PVADAT must be string
    if (pp.PP_PVADAT !== undefined && typeof pp.PP_PVADAT !== 'string') {
      pp.PP_PVADAT = String(pp.PP_PVADAT);
    }

    // PP: whitelist
    const allowedPP = new Set([
      'AA', 'BS', 'CA', 'CG', 'CL', 'EL', 'LC', 'LI', 'LP', 'OB', 'PH', 'QS', 'RI', 'RP', 'SV',
      'TP', 'VE', 'VL', 'VP', 'VW', 'WA',
      'PP_AFDDEFN', 'PP_AGNTNR', 'PP_BETTERM', 'PP_BMINSCH', 'PP_BMCOMTR', 'PP_BMEXTRA', 'PP_BMNAAND',
      'PP_BMPERC', 'PP_BRANCHE', 'PP_CDUUMND', 'PP_COLL', 'PP_EKST', 'PP_EXPDAT', 'PP_EXTERN',
      'PP_HANDACC', 'PP_HVVDAT', 'PP_IBAN', 'PP_INCWIJZ', 'PP_INGDAT', 'PP_INGDTW',
      'PP_IVBLETC', 'PP_MYAAND', 'PP_NCAAND', 'PP_NCPRC', 'PP_NTP', 'PP_PKRTVMP', 'PP_PKST', 'PP_PPRC',
      'PP_PRODNM', 'PP_PTSLTB', 'PP_SCHDVRY', 'PP_SPECLIM', 'PP_SPLIMNR', 'PP_STATUS', 'PP_STPRV',
      'PP_TARDAT', 'PP_TASS', 'PP_TKRTBM', 'PP_TPP', 'PP_TTOT', 'PP_TTPPRV',
      'PP_TTSLBM', 'PP_TTSLTB', 'PP_PVADAT',
    ]);
    for (const key of Object.keys(pp)) {
      // Preserve the _discountBaked marker so downstream pre-package discount blocks can
      // detect already-discounted data and skip double-applying.
      if (!allowedPP.has(key) && key !== '_discountBaked') delete pp[key];
    }

    // AFD convention: alphabetically sorted keys on outgoing documents (same as ongevallen).
    contract.PP = sortAfdDocument(contract.PP);
    if (contract.AL) contract.AL = sortAfdDocument(contract.AL);
  }

  return calculationDocument;
};

/**
 * SAA-specific: Enrich rechtsbijstand contract for SAA schema compliance.
 * Identified by presence of WC (Werkelijk Consumenten) section in PP.
 */
export const enrichRechtsbijstandForSaa = (calculationDocument: any, _ppEkst: number): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  for (const contract of calculationDocument.Contract) {
    // Identify rechtsbijstand by PP_EXTERN === 'RB'
    if (contract.PP?.PP_EXTERN !== 'RB') continue;

    // Strip response-only fields (Relatiedocument belongs at doc root, not inside a Contract item)
    delete contract.NT;
    delete contract.XG;
    delete contract.Supportsection;
    delete contract.Relatiedocument;

    // AL: keep BY array plus AL_APPLNM and AL_URLNAAM
    if (contract.AL) {
      const kept: any = {};
      if (contract.AL.AL_APPLNM) kept.AL_APPLNM = contract.AL.AL_APPLNM;
      if (contract.AL.AL_URLNAAM) kept.AL_URLNAAM = contract.AL.AL_URLNAAM;
      if (Array.isArray(contract.AL.BY) && contract.AL.BY.length > 0) kept.BY = contract.AL.BY;
      contract.AL = kept;
    }

    const pp = contract.PP;

    // VP: include VP_VRWRKCD
    pp.VP = { VP_VRWRKCD: pp.VP?.VP_VRWRKCD || '0' };

    // VE: VE_MYAAND + VE_VRWRKCD
    pp.VE = { VE_MYAAND: pp.VE?.VE_MYAAND || 'K781', VE_VRWRKCD: pp.VE?.VE_VRWRKCD || '0' };

    // VK: remove internal-only VK_GEZSAMST (used for GSS mapping, not valid for clientservice)
    if (pp.VK) delete pp.VK.VK_GEZSAMST;

    // WC: ensure correct values and preserve premium fields
    if (pp.WC) {
      pp.WC.WC_CODE = pp.WC.WC_CODE || '9901';
      pp.WC.WC_MYCODE = pp.WC.WC_MYCODE || '9330';
      pp.WC.WC_VZBFRNS = 0;
      pp.WC.WC_PPRC = 20;
      if (pp.WC.WC_VRWRKCD === undefined) pp.WC.WC_VRWRKCD = '0';
      if (!pp.WC.WC_WPREMBP) pp.WC.WC_WPREMBP = '4';
    }

    // Ensure required PP fields are set
    if (!pp.PP_BRANCHE) pp.PP_BRANCHE = '092';
    if (!pp.PP_MYAAND) pp.PP_MYAAND = 'P020';
    if (!pp.PP_EXTERN) pp.PP_EXTERN = 'RB';
    if (!pp.PP_HANDACC) pp.PP_HANDACC = 'N';
    if (!pp.PP_BETTERM) pp.PP_BETTERM = 1;
    applyPpDefaults(pp, RBV_PP_REQUEST_DEFAULTS);
    if (pp.PP_AFWAFST === undefined) pp.PP_AFWAFST = '';
    pp.PP_PPRC = 20;
    // PP_EKST - 0 at product level; charged once at package level (PK_TINV)
    pp.PP_EKST = 0;
    pp.PP_STPRV = 'J';

    if (!pp.IN) pp.IN = { IN_EIGBVAK: RBV_IN_EIGBVAK_DEFAULT };
    else if (!pp.IN.IN_EIGBVAK) pp.IN.IN_EIGBVAK = RBV_IN_EIGBVAK_DEFAULT;

    // Build BA array from the VP address (insured person address)
    const vpAddr = pp.VP || {};
    const baItem: any = {};
    if (vpAddr.VP_PCODE) baItem.BA_PCODE = vpAddr.VP_PCODE;
    if (vpAddr.VP_HUISNR) baItem.BA_HUISNR = Number(vpAddr.VP_HUISNR);
    if (vpAddr.VP_TOEVOEG) baItem.BA_TOEVOEG = vpAddr.VP_TOEVOEG;
    if (vpAddr.VP_STRAAT) baItem.BA_STRAAT = vpAddr.VP_STRAAT;
    if (vpAddr.VP_PLAATS) baItem.BA_PLAATS = vpAddr.VP_PLAATS;
    pp.BA = Object.keys(baItem).length > 0 ? [baItem] : [];

    // Strip internal/UI-only fields
    delete pp._rpEigendom;

    // PP whitelist
    const allowedPP = new Set([
      'BA', 'CL', 'FV', 'IN', 'PK', 'QS', 'RC', 'RI', 'RP', 'TP', 'VE', 'VK', 'VL', 'VP', 'VW', 'WC',
      'PP_ADEFVRS', 'PP_AFDDEFN', 'PP_AFWAFSP', 'PP_AFWAFST', 'PP_AGNTNR',
      'PP_BETTERM', 'PP_BRANCHE', 'PP_BTP', 'PP_BTWCD', 'PP_CDUUMND', 'PP_COLL', 'PP_CVS', 'PP_EKST',
      'PP_EXPDAT', 'PP_EXTERN', 'PP_HANDACC', 'PP_HVVDAT', 'PP_IBAN', 'PP_INCWIJZ',
      'PP_INGDAT', 'PP_INGDTW', 'PP_MYAAND', 'PP_MYNAAM', 'PP_NTP', 'PP_PKRTVMP',
      'PP_PKST', 'PP_PPRC', 'PP_PRODNM', 'PP_PRODUCT', 'PP_PTSLTB', 'PP_PVADAT', 'PP_SLOTAKK', 'PP_SPECLIM',
      'PP_SPLIMNR', 'PP_STATUS', 'PP_STPRV', 'PP_TARDAT', 'PP_TASS', 'PP_TKRT',
      'PP_TPKRTVM', 'PP_TPP', 'PP_TTOT', 'PP_TTPPRV', 'PP_TTSL', 'PP_TTSLTB', 'PP_VRWRKCD', 'PP_WPREMBP',
    ]);
    for (const key of Object.keys(pp)) {
      // Preserve the _discountBaked marker so downstream pre-package discount blocks can
      // detect already-discounted data and skip double-applying.
      if (!allowedPP.has(key) && key !== '_discountBaked') delete pp[key];
    }
  }

  return calculationDocument;
};

// Doorlopende reis (DFD/K781): coverage rides under PP.DR (or PP.OD if the clientservice
// chain expects the schema's Ongevallen section). A single helper keeps detection consistent
// everywhere so DR/OD never drift apart.
export const drCoverage = (pp: any): any => pp?.DR || pp?.OD;

/**
 * SAA-specific: enrich/whitelist the Doorlopende reis (DFD/K781) contract for the
 * clientservice chain. Mirrors enrichAvpForSaa: keep only DFD-schema fields, set the
 * K781 maatschappij codes, default the acceptance fields, and force PP_STPRV='J'
 * (the one product whose 'J' default is correct).
 */
export const enrichDoorlopendeReisForSaa = (calculationDocument: any, workflowData: any, opts: EnrichOpts): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  const appName = calculationDocument?.Relatiemantel?.AL?.AL_APPLNM || 'SAA Verzekeringen';
  const baseUrl = calculationDocument?.Relatiemantel?.AL?.AL_URLNAAM || opts.baseUrl;

  for (const contract of calculationDocument.Contract) {
    const pp = contract.PP;
    // DR only — a bare OD section (no DR) is the Ongevallen product (enrichOngevallenForSaa).
    if (!pp || !pp.DR) continue;
    if (pp.DA || pp.IP || pp.OP || pp.OB || pp.WC) continue; // not the DR contract

    const wfPP = workflowData?.['doorlopendereis']?.Contractdocument?.PP || {};
    const wfAL = workflowData?.['doorlopendereis']?.Contractdocument?.AL || {};

    // --- AL: whitelist ---
    const rawAL = { ...contract.AL };
    const cleanAL: any = {};
    cleanAL.AL_APPLNM = rawAL.AL_APPLNM || wfAL.AL_APPLNM || appName;
    cleanAL.AL_URLNAAM = rawAL.AL_URLNAAM || wfAL.AL_URLNAAM || baseUrl;
    const rawBY = rawAL.BY || wfAL.BY || [];
    if (Array.isArray(rawBY) && rawBY.length > 0) {
      const byKeys = ['BY_URLBY', 'BY_BYLOMS', 'BY_BYLSRT'];
      cleanAL.BY = rawBY.map((item: any) => {
        const c: any = {};
        for (const k of byKeys) if (item[k] !== undefined) c[k] = k === 'BY_BYLSRT' ? String(item[k]) : item[k];
        return c;
      });
    }
    contract.AL = cleanAL;

    // --- VP: policyholder address (no risk BA for travel) ---
    const rawVP = (wfPP.VP && Object.keys(wfPP.VP).length > 0) ? wfPP.VP : pp.VP || {};
    const vpKeys = ['VP_ANAAM', 'VP_GEBDAT', 'VP_NRHNDRG', 'VP_PCODE', 'VP_STRAAT', 'VP_PLAATS', 'VP_HUISNR', 'VP_TOEVOEG'];
    const cleanVP: any = {};
    for (const k of vpKeys) if (rawVP[k] !== undefined) cleanVP[k] = rawVP[k];
    contract.PP.VP = cleanVP;

    // --- RI: default the DR acceptance fields to 'N' ---
    const riKeys = ['RI_OGBOOIT', 'RI_OVRMED', 'RI_POLJUS8'];
    const rawRI = pp.RI || wfPP.RI || {};
    const cleanRI: any = {};
    for (const k of riKeys) cleanRI[k] = rawRI[k] ?? 'N';
    contract.PP.RI = cleanRI;

    // --- VE / VL: K781 maatschappij ---
    const rawVE = pp.VE || wfPP.VE || {};
    const cleanVE: any = {};
    if (rawVE.VE_VOLGNUM !== undefined) cleanVE.VE_VOLGNUM = rawVE.VE_VOLGNUM;
    cleanVE.VE_MYAAND = DOORLOPENDE_REIS_CARRIER;
    contract.PP.VE = cleanVE;
    const rawVL = (wfPP.VL && Object.keys(wfPP.VL).length > 0) ? wfPP.VL : pp.VL || {};
    const cleanVL: any = {};
    if (rawVL.VL_VOLGNUM !== undefined) cleanVL.VL_VOLGNUM = rawVL.VL_VOLGNUM;
    cleanVL.VL_MYAAND = DOORLOPENDE_REIS_CARRIER;
    contract.PP.VL = cleanVL;

    // --- CL / VW: whitelist items ---
    const clKeys = ['CL_VOLGNUM', 'CL_CLAUSNR', 'CL_CLAUSOM', 'CL_CLAUTXT', 'CL_VERSIEC'];
    if (Array.isArray(pp.CL)) {
      contract.PP.CL = pp.CL.map((item: any) => {
        const c: any = {};
        for (const k of clKeys) if (item[k] !== undefined) c[k] = item[k];
        return c;
      });
    }
    const srcVW = (wfPP.VW && Array.isArray(wfPP.VW)) ? wfPP.VW : pp.VW || [];
    const vwKeys = ['VW_VOLGNUM', 'VW_VRWNR', 'VW_VRWOM', 'VW_VRWVERS', 'VW_BRONVRW'];
    if (Array.isArray(srcVW)) {
      contract.PP.VW = srcVW.map((item: any) => {
        const c: any = {};
        for (const k of vwKeys) if (item[k] !== undefined) c[k] = item[k];
        return c;
      });
    }
    contract.PP.TP = {};

    // --- PP scalars: whitelist to DFD-schema fields only ---
    const ppScalarKeys = [
      'PP_EXTERN', 'PP_BRANCHE', 'PP_MYAAND', 'PP_INGDAT', 'PP_INGDTW', 'PP_CDUUMND',
      'PP_HVVDAT', 'PP_BETTERM', 'PP_STATUS', 'PP_AFDDEFN', 'PP_ADEFVRS', 'PP_AFDDTAG',
      'PP_HANDACC', 'PP_SLOTAKK', 'PP_SPECLIM', 'PP_SPLIMNR', 'PP_AFWAFSP', 'PP_AFWAFST',
      'PP_PRODNM', 'PP_PRODUCT', 'PP_IBAN', 'PP_STPRV', 'PP_BTWCD', 'PP_MYNAAM', 'PP_NUMMER',
      'PP_VGCODE', 'PP_PVADAT', 'PP_EXPDAT', 'PP_NTP', 'PP_BTP', 'PP_TASS', 'PP_TTOT', 'PP_POLPKJN',
    ];
    const cleanPP: any = {};
    for (const k of ppScalarKeys) {
      if (pp[k] !== undefined) cleanPP[k] = pp[k];
      else if (wfPP[k] !== undefined) cleanPP[k] = wfPP[k];
    }
    if (!cleanPP.PP_STPRV) cleanPP.PP_STPRV = 'J';
    if (!cleanPP.PP_POLPKJN) cleanPP.PP_POLPKJN = 'J';
    applyPpDefaults(cleanPP, DOORLOPENDE_REIS_PP_REQUEST_DEFAULTS);
    // documentTemplate.json ships PP_BRANCHE/PP_AFDDEFN as '' (defined, so the whitelist copies
    // them); the '601' values only overlay on the SCREEN's calculate payload (doorlopendereis/
    // calc.ts). A stored doc that never re-ran calculate reaches this chain with '' — fall back
    // like enrichRechtsbijstandForSaa does for '092', single-sourced from the DR defaults.
    if (!cleanPP.PP_BRANCHE) cleanPP.PP_BRANCHE = String(DOORLOPENDE_REIS_DEFAULTS.PP_BRANCHE);
    if (!cleanPP.PP_AFDDEFN) cleanPP.PP_AFDDEFN = String(DOORLOPENDE_REIS_DEFAULTS.PP_AFDDEFN);
    // Risicodrager/maatschappij: ALWAYS the configured value, never a guarded fallback. A stored
    // document can carry an engine-echoed or pre-change 'K781' here, and `if (!x)` would keep it.
    // Same rule as ongevallen; VE_MYAAND stays the carrier (K781).
    cleanPP.PP_MYAAND = DOORLOPENDE_REIS_MYAAND;
    cleanPP.PP_MYNAAM = DOORLOPENDE_REIS_MYAAND;
    if (!cleanPP.PP_PRODUCT && cleanPP.PP_PRODNM) cleanPP.PP_PRODUCT = cleanPP.PP_PRODNM;
    if (cleanPP.PP_PVADAT !== undefined && typeof cleanPP.PP_PVADAT !== 'string') cleanPP.PP_PVADAT = String(cleanPP.PP_PVADAT);
    if (cleanPP.PP_EXPDAT !== undefined && typeof cleanPP.PP_EXPDAT !== 'string') cleanPP.PP_EXPDAT = String(cleanPP.PP_EXPDAT);

    // Re-attach the coverage section + sub-objects. DR_MVZBMIL has no UI on this product but the
    // backend needs it on every document; the chain responses don't echo it — re-assert 'N'.
    if (pp.DR) cleanPP.DR = restoreDoorlopendeReisInputs(pp.DR);
    if (pp.OD) cleanPP.OD = pp.OD;
    cleanPP.RI = contract.PP.RI;
    cleanPP.VP = contract.PP.VP;
    cleanPP.VE = contract.PP.VE;
    cleanPP.VL = contract.PP.VL;
    cleanPP.TP = contract.PP.TP;
    if (contract.PP.CL) cleanPP.CL = contract.PP.CL;
    if (contract.PP.VW) cleanPP.VW = contract.PP.VW;
    if (contract.PP.QS) cleanPP.QS = contract.PP.QS;

    contract.PP = cleanPP;
  }

  return calculationDocument;
};

/**
 * SAA-specific: enrich/whitelist the Ongevallen (DFD/K781, branche 011) contract for the
 * clientservice chain. Mirrors enrichDoorlopendeReisForSaa — same DFD envelope — with OD as
 * the real coverage section and the VZ (beroepsmilitair) block preserved.
 * TODO(refactor): fold both DFD twins into the generic registry-driven enrich (S3).
 */
export const enrichOngevallenForSaa = (calculationDocument: any, workflowData: any, opts: EnrichOpts): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  const appName = calculationDocument?.Relatiemantel?.AL?.AL_APPLNM || 'SAA Verzekeringen';
  const baseUrl = calculationDocument?.Relatiemantel?.AL?.AL_URLNAAM || opts.baseUrl;

  for (const contract of calculationDocument.Contract) {
    const pp = contract.PP;
    if (!pp || !pp.OD || pp.DR) continue; // bare OD = ongevallen; OD alongside DR = doorlopendereis
    if (pp.DA || pp.IP || pp.OP || pp.OB || pp.WC) continue; // not the ongevallen contract

    const wfPP = workflowData?.['ongevallen']?.Contractdocument?.PP || {};
    const wfAL = workflowData?.['ongevallen']?.Contractdocument?.AL || {};

    const rawAL = { ...contract.AL };
    const cleanAL: any = {};
    cleanAL.AL_APPLNM = rawAL.AL_APPLNM || wfAL.AL_APPLNM || appName;
    cleanAL.AL_URLNAAM = rawAL.AL_URLNAAM || wfAL.AL_URLNAAM || baseUrl;
    const rawBY = rawAL.BY || wfAL.BY || [];
    if (Array.isArray(rawBY) && rawBY.length > 0) {
      const byKeys = ['BY_URLBY', 'BY_BYLOMS', 'BY_BYLSRT'];
      cleanAL.BY = rawBY.map((item: any) => {
        const c: any = {};
        for (const k of byKeys) if (item[k] !== undefined) c[k] = k === 'BY_BYLSRT' ? String(item[k]) : item[k];
        return c;
      });
    }
    contract.AL = cleanAL;

    // --- VP: policyholder address (no risk BA) ---
    const relVP = workflowData?.['ongevallen']?.Relatiedocument?.VP || {};
    const rawVP = { ...relVP, ...((wfPP.VP && Object.keys(wfPP.VP).length > 0) ? wfPP.VP : pp.VP || {}) };
    const vpKeys = ['VP_ANAAM', 'VP_GEBDAT', 'VP_NRHNDRG', 'VP_PCODE', 'VP_STRAAT', 'VP_PLAATS', 'VP_HUISNR', 'VP_TOEVOEG'];
    const cleanVP: any = {};
    for (const k of vpKeys) if (rawVP[k] !== undefined) cleanVP[k] = rawVP[k];
    if (typeof cleanVP.VP_HUISNR === 'string' && cleanVP.VP_HUISNR.trim() !== '' && !isNaN(Number(cleanVP.VP_HUISNR))) {
      cleanVP.VP_HUISNR = Number(cleanVP.VP_HUISNR);
    }
    contract.PP.VP = cleanVP;

    // --- RI: default the acceptance fields to 'N' (same fields as DR) ---
    const riKeys = ['RI_OGBOOIT', 'RI_OVRMED', 'RI_POLJUS8'];
    const rawRI = pp.RI || wfPP.RI || {};
    const cleanRI: any = {};
    for (const k of riKeys) cleanRI[k] = rawRI[k] ?? 'N';
    contract.PP.RI = cleanRI;

    // --- VZ: beroepsmilitair block (this product's extra section vs DR) + the verzekerde's
    // identity ANVA needs on the request: VZ_ANAAM/VZ_VOORL from the relation VP, VZ_GEBDAT
    // from the contract VP. The calculate schema forbids the name fields on VZ, so they exist
    // on the chain/request documents only — see restoreOngevallenInsured. ---
    const rawVZ = (wfPP.VZ && Object.keys(wfPP.VZ).length > 0) ? wfPP.VZ : pp.VZ || {};
    const vzKeys = ['VZ_GEBDAT', 'VZ_BERMIL', 'VZ_BERMILP', 'VZ_ANAAM', 'VZ_VOORL'];
    const cleanVZ: any = {};
    for (const k of vzKeys) if (rawVZ[k] !== undefined) cleanVZ[k] = rawVZ[k];
    const relationVP = calculationDocument?.Relatiemantel?.VP || {};
    contract.PP.VZ = restoreOngevallenInsured(cleanVZ, {
      lastName: relationVP.VP_ANAAM ?? cleanVP.VP_ANAAM,
      initials: relationVP.VP_VOORL,
      gebdat: cleanVP.VP_GEBDAT,
    });

    // --- VE / VL: K781 maatschappij ---
    const rawVE = pp.VE || wfPP.VE || {};
    const cleanVE: any = {};
    if (rawVE.VE_VOLGNUM !== undefined) cleanVE.VE_VOLGNUM = rawVE.VE_VOLGNUM;
    cleanVE.VE_MYAAND = ONGEVALLEN_CARRIER;
    contract.PP.VE = cleanVE;
    const rawVL = (wfPP.VL && Object.keys(wfPP.VL).length > 0) ? wfPP.VL : pp.VL || {};
    const cleanVL: any = {};
    if (rawVL.VL_VOLGNUM !== undefined) cleanVL.VL_VOLGNUM = rawVL.VL_VOLGNUM;
    cleanVL.VL_MYAAND = ONGEVALLEN_CARRIER;
    contract.PP.VL = cleanVL;

    const clKeys = ['CL_VOLGNUM', 'CL_CLAUSNR', 'CL_CLAUSOM', 'CL_CLAUTXT', 'CL_VERSIEC', 'CL_CLAUVRW'];
    if (Array.isArray(pp.CL)) {
      contract.PP.CL = pp.CL.map((item: any) => {
        const c: any = {};
        for (const k of clKeys) if (item[k] !== undefined) c[k] = item[k];
        return c;
      });
    }
    const srcVW = (wfPP.VW && Array.isArray(wfPP.VW)) ? wfPP.VW : pp.VW || [];
    const vwKeys = ['VW_VOLGNUM', 'VW_VRWNR', 'VW_VRWOM', 'VW_VRWOMS', 'VW_VRWVERS', 'VW_BRONVRW'];
    if (Array.isArray(srcVW)) {
      contract.PP.VW = srcVW.map((item: any) => {
        const c: any = {};
        for (const k of vwKeys) if (item[k] !== undefined) c[k] = item[k];
        return c;
      });
    }
    contract.PP.TP = {};

    // --- PP scalars: whitelist to the ongevallen-schema fields only ---
    const ppScalarKeys = [
      'PP_EXTERN', 'PP_BRANCHE', 'PP_MYAAND', 'PP_INGDAT', 'PP_INGDTW', 'PP_CDUUMND',
      'PP_HVVDAT', 'PP_BETTERM', 'PP_STATUS', 'PP_AFDDEFN', 'PP_ADEFVRS', 'PP_AFDDTAG',
      'PP_HANDACC', 'PP_SLOTAKK', 'PP_SPECLIM', 'PP_SPLIMNR', 'PP_AFWAFSP', 'PP_AFWAFST',
      'PP_PRODNM', 'PP_PRODUCT', 'PP_IBAN', 'PP_STPRV', 'PP_BTWCD', 'PP_MYNAAM', 'PP_NUMMER',
      'PP_VGCODE', 'PP_PVADAT', 'PP_EXPDAT', 'PP_NTP', 'PP_BTP', 'PP_TASS', 'PP_TTOT', 'PP_POLPKJN',
    ];
    const cleanPP: any = {};
    for (const k of ppScalarKeys) {
      if (pp[k] !== undefined) cleanPP[k] = pp[k];
      else if (wfPP[k] !== undefined) cleanPP[k] = wfPP[k];
    }
    if (!cleanPP.PP_STPRV) cleanPP.PP_STPRV = 'J';
    if (!cleanPP.PP_POLPKJN) cleanPP.PP_POLPKJN = 'J';
    // Includes the PP_BTWCD fallback the DR chain already had (empty-template hole)
    applyPpDefaults(cleanPP, ONGEVALLEN_PP_REQUEST_DEFAULTS);
    // Same empty-template hole as DR: a stored doc that never re-ran calculate reaches this
    // chain with '' — fall back to the schema defaults, single-sourced from fields.ts.
    if (!cleanPP.PP_BRANCHE) cleanPP.PP_BRANCHE = String(ONGEVALLEN_DEFAULTS.PP_BRANCHE);
    if (!cleanPP.PP_AFDDEFN) cleanPP.PP_AFDDEFN = String(ONGEVALLEN_DEFAULTS.PP_AFDDEFN);
    cleanPP.PP_BETTERM = Number(ONGEVALLEN_DEFAULTS.PP_BETTERM);
    cleanPP.PP_MYAAND = String(ONGEVALLEN_DEFAULTS.PP_MYAAND);
    cleanPP.PP_MYNAAM = String(ONGEVALLEN_DEFAULTS.PP_MYNAAM);
    if (!cleanPP.PP_PRODUCT && cleanPP.PP_PRODNM) cleanPP.PP_PRODUCT = cleanPP.PP_PRODNM;
    if (cleanPP.PP_PVADAT !== undefined && typeof cleanPP.PP_PVADAT !== 'string') cleanPP.PP_PVADAT = String(cleanPP.PP_PVADAT);
    if (cleanPP.PP_EXPDAT !== undefined && typeof cleanPP.PP_EXPDAT !== 'string') cleanPP.PP_EXPDAT = String(cleanPP.PP_EXPDAT);

    // Re-attach the coverage section + sub-objects. The chain responses don't echo the
    // input-only OD fields (OD_COMBINA '1'-'6', the sums) — re-assert them from the screen's
    // values so a stored contract never reaches ANVA without the chosen combination.
    cleanPP.OD = restoreOngevallenInputs(pp.OD, workflowData?.['ongevallen']?.values);
    cleanPP.RI = contract.PP.RI;
    cleanPP.VP = contract.PP.VP;
    cleanPP.VZ = contract.PP.VZ;
    cleanPP.VE = contract.PP.VE;
    cleanPP.VL = contract.PP.VL;
    cleanPP.TP = contract.PP.TP;
    if (contract.PP.CL) cleanPP.CL = contract.PP.CL;
    if (contract.PP.VW) cleanPP.VW = contract.PP.VW;

    contract.PP = cleanPP;
    normalizeOngevallenVwCodes(contract); // ANVA-safe voorwaarden code (see calc.ts)
    // AFD convention: alphabetically sorted keys on every outgoing ongevallen document
    // (this enriched contract feeds the relation/package/offer/quote payloads).
    contract.PP = sortAfdDocument(contract.PP);
    contract.AL = sortAfdDocument(contract.AL);
  }

  return calculationDocument;
};

/**
 * SAA-specific: Coerce all schema-defined numeric fields from strings to numbers.
 * BFF returns many numeric fields as strings; this single pass fixes them all
 * across every product contract, eliminating string concatenation bugs in premium calculations.
 */
export const coerceNumericFields = (calculationDocument: any): any => {
  if (!calculationDocument?.Contract) return calculationDocument;

  // Suffixes that are always numeric per product schemas (number or integer type)
  const numericSuffixes = new Set([
    // Premium/financial fields
    '_BTP', '_NTP', '_TPP', '_TASS', '_TTOT', '_TKRT', '_TTSL', '_EKST',
    '_TTPPRV', '_TTSLTB', '_PTSLTB', '_PKRTVMP', '_TPKRTVM', '_TKRTBM', '_TTSLBM',
    '_PKST',
    // Coverage/deductible fields
    '_ERB', '_ERBSTRM', '_VEERB', '_PPRC', '_VERZSOM', '_VZBFRNS', '_EIGBVAK',
    // Count/sequence fields
    '_VOLGNUM',
    // Date fields (schema: number)
    '_GEBDAT', '_HVVDAT', '_INGDAT', '_TARDAT', '_INGDTW', '_DATTOEL',
    // Other integer fields
    '_BETTERM', '_CDUUMND', '_HUISNR', '_OPZPKVA', '_BGM2', '_TOPPBGB',
    '_BMCOMTR', '_BMEXTRA', '_BMINSCH', '_BMNAAND', '_BMPERC', '_NCAAND', '_NCPRC', '_SCHDVRY',
    '_VBE', '_VGAVRH', '_VWE', '_AANTKW', '_ADEUR', '_BOUWMND',
  ]);

  // Full field names that are numeric but don't follow a suffix pattern
  const numericFullNames = new Set([
    'PP_NTP', 'PP_NTPVPK', 'PP_BTP', 'PP_TPP', 'PP_TASS', 'PP_TTOT', 'PP_TKRT',
    'PP_TTSL', 'PP_EKST', 'PP_TTPPRV', 'PP_TTSLTB', 'PP_PTSLTB', 'PP_PKRTVMP',
    'PP_PKST', 'PP_PPRC', 'PP_HVVDAT', 'PP_INGDAT', 'PP_TARDAT',
    'PP_INGDTW', 'PP_CDUUMND', 'PP_BETTERM',
    'BA_HUISNR', 'BA_OPZPKVA', 'BA_BGM2', 'BA_TOPPBGB', 'BA_HERBWRD', 'BA_BOUWJR',
  ]);

  const isNumericField = (key: string): boolean => {
    if (numericFullNames.has(key)) return true;
    const underscoreIdx = key.lastIndexOf('_');
    if (underscoreIdx > 0) {
      const suffix = key.substring(underscoreIdx);
      return numericSuffixes.has(suffix);
    }
    return false;
  };

  const coerceObject = (obj: any): void => {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) {
      for (const item of obj) coerceObject(item);
      return;
    }
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (val !== null && val !== undefined && typeof val === 'string' && isNumericField(key)) {
        const num = Number(val);
        if (!isNaN(num)) obj[key] = num;
      } else if (val && typeof val === 'object') {
        coerceObject(val);
      }
    }
  };

  // Coerce Relatiemantel
  if (calculationDocument.Relatiemantel) {
    coerceObject(calculationDocument.Relatiemantel);
  }

  // Coerce all Contract items
  for (const contract of calculationDocument.Contract) {
    coerceObject(contract);
  }

  // Coerce Pakket if present
  if (calculationDocument.Pakket) {
    coerceObject(calculationDocument.Pakket);
  }

  return calculationDocument;
};

/**
 * SAA-specific: Strip Supportsection from all contract items before sending to services.
 * Supportsection is UI-only state (coverage selections) and not accepted by the API.
 */
export const stripSupportsection = (calculationDocument: any): any => {
  if (!calculationDocument?.Contract) return calculationDocument;
  for (const contract of calculationDocument.Contract) {
    delete contract.Supportsection;
    // Strip response-only Contractdocument-level extras the BFF re-introduces on merges
    delete contract.NT;
    delete contract.XG;
    delete contract.Relatiedocument;
    // Strip fields BFF returns but schema does not allow
    if (contract.PP) {
      delete contract.PP.PP_TINV;
      delete contract.PP.PP_TPP;
    }
  }
  return calculationDocument;
};

// The full SAA enrichment pipeline applied to every buildCalculationDocument output.
// Composed ONCE here — the screen previously inlined this 8-deep chain three times.
export const enrichCalculationDocumentForSaa = (
  calculationDocument: any,
  workflowData: any,
  t: (key: string) => string,
  ppEkst: number,
  opts: EnrichOpts,
): any =>
  orderSaaCoveragesInDocument(
    stripSupportsection(
      coerceNumericFields(
        enrichDoorlopendeReisForSaa(
          enrichOngevallenForSaa(
            enrichRechtsbijstandForSaa(
              enrichPersonenautoForSaa(
                enrichWoonhuisForSaa(
                  enrichInboedelForSaa(
                    enrichAvpForSaa(calculationDocument, workflowData, t, ppEkst, opts),
                    workflowData,
                    ppEkst,
                    opts,
                  ),
                  workflowData,
                  ppEkst,
                ),
                ppEkst,
              ),
              ppEkst,
            ),
            workflowData,
            opts,
          ),
          workflowData,
          opts,
        ),
      ),
    ),
  );
